import { getOwnerDocument } from '@react-aria/utils';
import * as React from 'react';

import { useEventCallback } from '@gitbook/util-react-hooks';

/**
 * A hook that listens for context menu events outside of a given ref.
 */
export function useContextMenuOutside(props: {
    ref: React.RefObject<Element | null>;
    onInteractOutside: (event: MouseEvent) => void;
    isDisabled?: boolean;
}): void {
    const { ref, onInteractOutside, isDisabled } = props;

    const onContextMenu = useEventCallback((event: MouseEvent) => {
        if (isValidEvent(event, ref)) {
            onInteractOutside(event);
        }
    });

    React.useEffect(() => {
        if (isDisabled) {
            return;
        }

        const element = ref.current;
        const documentObject = getOwnerDocument(element);

        documentObject.addEventListener('contextmenu', onContextMenu, true);

        return () => {
            documentObject.removeEventListener('contextmenu', onContextMenu, true);
        };
    }, [ref, isDisabled, onContextMenu]);
}

function isValidEvent(event: MouseEvent, ref: React.RefObject<Element | null>) {
    if (event.target && event.target instanceof Element) {
        // if the event target is no longer in the document, ignore
        const ownerDocument = event.target.ownerDocument;
        if (!ownerDocument || !ownerDocument.documentElement.contains(event.target)) {
            return false;
        }
    }

    if (!ref.current) {
        return false;
    }

    // When the event source is inside a Shadow DOM, event.target is just the shadow root.
    // Using event.composedPath instead means we can get the actual element inside the shadow root.
    // This only works if the shadow root is open, there is no way to detect if it is closed.
    // If the event composed path contains the ref, interaction is inside.
    return !event.composedPath().includes(ref.current);
}
